import { useEffect, useState } from "react";
import { fetchFileObjectUrl } from "@/api/files";
import type { Attachment } from "@/api/content";
import { useLang } from "@/i18n/LanguageContext";

/**
 * Renders one attachment. The bytes need a Bearer token, so we fetch them via the
 * API client into an object URL: PDFs embed inline, images render, anything else
 * becomes a download link. The object URL is revoked on unmount.
 */
export function FilePreview({ att }: { att: Attachment }) {
  const { t } = useLang();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let created: string | null = null;
    setUrl(null);
    setError(false);
    fetchFileObjectUrl(att.file_id)
      .then((u) => {
        if (active) {
          created = u;
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [att.file_id]);

  const isPdf = att.mime === "application/pdf";
  const isImage = att.mime.startsWith("image/");

  return (
    <figure className="attachment">
      {att.caption && <figcaption className="attachment-cap">{att.caption}</figcaption>}
      {error ? (
        <p className="form-error">{t("Kon bestand niet laden.", "Could not load file.")}</p>
      ) : !url ? (
        <p className="attachment-loading">{t("Laden…", "Loading…")}</p>
      ) : isPdf ? (
        <embed className="attachment-pdf" src={url} type="application/pdf" />
      ) : isImage ? (
        <img className="attachment-img" src={url} alt={att.caption || att.filename} />
      ) : null}
      {url && (
        <a className="attachment-dl" href={url} download={att.filename}>⬇ {att.filename}</a>
      )}
    </figure>
  );
}
